import { OptimizationData } from '../types';
import { AIError, classifyAIError, validateAIResponse } from '../utils/aiErrorHandler';
import { auth } from '../firebase';

const AI_STREAM_TIMEOUT_MS = 60000; // 60초 타임아웃

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
  } catch (e) {
    console.warn('[Gemini Service] Failed to get auth token:', e);
  }
  return headers;
}

async function withRetry<T>(fn: () => Promise<T>, retries = 3, delay = 3000): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    const errorMsg = error?.message || '';
    const errorStr = JSON.stringify(error);
    const isRateLimit =
      errorMsg.includes('429') ||
      errorMsg.includes('RESOURCE_EXHAUSTED') ||
      errorStr.includes('429') ||
      errorStr.includes('RESOURCE_EXHAUSTED');

    if (retries > 0 && isRateLimit) {
      console.warn(`API rate limited. Retrying in ${delay}ms... (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return withRetry(fn, retries - 1, delay * 2);
    }
    throw error;
  }
}

async function streamFetch(
  url: string, 
  body: any, 
  onChunk: (text: string) => void, 
  signal?: AbortSignal, 
  onData?: (data: any) => void
) {
  // 60초 타임아웃 AbortSignal 생성 (외부 signal과 병합)
  const timeoutSignal = AbortSignal.timeout(AI_STREAM_TIMEOUT_MS);

  // 외부 signal이 있으면 두 signal을 합성
  const combinedSignal = signal
    ? combineAbortSignals(signal, timeoutSignal)
    : timeoutSignal;

  const headers = await getAuthHeaders();

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
    signal: combinedSignal
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const errorMsg = (err as any).message || (err as any).error || (err as any).reason || `HTTP ${response.status}`;
    const requiredTier = (err as any).requiredTier;
    // classifyAIError로 에러 분류하여 AIError throw
    const aiErrorInfo = classifyAIError(errorMsg, body.language || 'ko');
    throw new AIError(aiErrorInfo.code, body.language || 'ko', errorMsg, requiredTier);
  }

  if (!response.body) {
    throw new AIError('NETWORK_ERROR', body.language || 'ko', 'No response body from AI server. Check CORS/Network.');
  }

  // SES (Hardened JS) compatibility check
  if (typeof (response.body as any).getReader !== 'function') {
    throw new AIError('NETWORK_ERROR', body.language || 'ko', 'Streaming not supported in this browser environment (SES/Secure JS restriction).');
  }

  const reader = response.body.getReader();
  if (!reader) {
    throw new AIError('NETWORK_ERROR', body.language || 'ko', 'Failed to create stream reader');
  }

  const onAbort = () => {
    reader.cancel().catch(() => { });
  };

  if (combinedSignal) {
    if (combinedSignal.aborted) {
      reader.cancel().catch(() => { });
      // 타임아웃에 의한 중단인지 확인
      if (timeoutSignal.aborted) {
        throw new AIError('TIMEOUT', body.language || 'ko');
      }
      throw new DOMException('Aborted', 'AbortError');
    }
    combinedSignal.addEventListener('abort', onAbort);
  }

  let receivedAnyChunk = false;

  try {
    const decoder = new TextDecoder();
    let buffer = '';

// eslint-disable-next-line
    while (true) {
      if (combinedSignal?.aborted) {
        if (timeoutSignal.aborted) {
          throw new AIError('TIMEOUT', body.language || 'ko');
        }
        throw new DOMException('Aborted', 'AbortError');
      }
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.replace('data: ', '');
          if (jsonStr === '[DONE]') continue;
          try {
            const data = JSON.parse(jsonStr);
            if (data.error) {
              const aiErrorInfo = classifyAIError(data.error, body.language || 'ko');
              throw new AIError(aiErrorInfo.code, body.language || 'ko', data.error);
            }
            if (data.indicator_results && onData) {
              onData(data);
            }
            if (data.text) {
              receivedAnyChunk = true;
              onChunk(data.text);
            }
          } catch (e: any) {
            // AIError는 그대로 throw, 나머지는 Skip malformed JSON
            if (e instanceof AIError || e?.name === 'AIError') throw e;
          }
        }
      }
    }

    // 스트림이 정상 종료되었으나 청크를 하나도 받지 못한 경우
    if (!receivedAnyChunk) {
      throw new AIError('EMPTY_STREAM', body.language || 'ko');
    }
  } finally {
    if (combinedSignal) {
      combinedSignal.removeEventListener('abort', onAbort);
    }
  }
}

/**
 * 두 개의 AbortSignal을 합성하는 헬퍼 함수
 */
function combineAbortSignals(s1: AbortSignal, s2: AbortSignal): AbortSignal {
  const controller = new AbortController();

  const onAbort = () => {
    controller.abort();
  };

  s1.addEventListener('abort', onAbort);
  s2.addEventListener('abort', onAbort);

  // 이미 중단된 signal이 있으면 즉시 중단
  if (s1.aborted || s2.aborted) {
    controller.abort();
  }

  return controller.signal;
}


export async function analyzeMacro(data: any, language: string = 'ko', onChunk: (t: string) => void, signal?: AbortSignal, reportFormat: string = 'text', targetMarket: string = 'US'): Promise<void> {
  await streamFetch('/api/ai/analyze-macro', { data, language, reportFormat, targetMarket }, onChunk, signal);
}

export async function analyzeStock(symbol: string, macro: any, signal: any, alternativeData: any, analysisType: string, language: string, onChunk: (t: string) => void, abortSignal?: AbortSignal, reportFormat: string = 'text'): Promise<void> {
  await streamFetch('/api/ai/analyze-stock', { symbol, macro, signal, alternativeData, analysisType, language, reportFormat }, onChunk, abortSignal);
}

export async function getPortfolioAdvice(portfolio: any[], macro: any, language: string = 'ko', onChunk: (t: string) => void, signal?: AbortSignal, macroSummary?: string, targetGoalAmount?: number, investorType?: number, targetMarket?: string, reportFormat: string = 'text', category: string = 'portfolio'): Promise<void> {
  await streamFetch('/api/ai/analyze-portfolio', { portfolio, macro, language, executiveSummary: macroSummary, targetGoalAmount, investorType, targetMarket, reportFormat, category }, onChunk, signal);
}

export async function streamPortfolioXRayReport(
  xrayData: any,
  macro: any,
  language: string = 'ko',
  onChunk: (t: string) => void,
  signal?: AbortSignal
): Promise<void> {
  const { apparentHoldings, trueHoldings, trueSectors, riskAlerts, totalValue } = xrayData;
  await streamFetch('/api/ai/analyze-portfolio', {
    apparentHoldings,
    trueHoldings,
    trueSectors,
    riskAlerts,
    totalValue,
    macro,
    language,
    category: 'portfolio_xray'
  }, onChunk, signal);
}


export async function analyzeEvent(event: any, portfolio: any[], macro: any, language: string = 'ko', onChunk: (t: string) => void, signal?: AbortSignal, marketMode: string = 'US', onIndicatorData?: (data: any) => void): Promise<void> {
  await streamFetch('/api/ai/analyze-event', { event, portfolio, macro, language, marketMode }, onChunk, signal, onIndicatorData);
}

export async function aggregateIntelligence() {
  const url = '/api/intelligence/market-data';
  const headers = await getAuthHeaders();
  const response = await fetch(url, { method: 'POST', headers, credentials: 'include' });
  if (!response.ok) throw new Error(`Market Data failed: ${response.status}`);
  return response.json();
}

export async function analyzeIntelligence(macro: any, intelligence: any, language: string = 'ko', onChunk: (t: string) => void, executiveSummary?: string, abortSignal?: AbortSignal, reportFormat: string = 'text', targetMarket: string = 'US'): Promise<void> {
  await streamFetch('/api/ai/analyze-intelligence', { macro, intelligence, language, executiveSummary, reportFormat, targetMarket }, onChunk, abortSignal);
}

export async function optimizePortfolio(
  portfolio: any[],
  macro: any,
  language: string = 'ko',
  investorType?: number,
  onProgress?: (percent: number, message: string) => void,
  signal?: AbortSignal
): Promise<OptimizationData> {
  const headers = await getAuthHeaders();
  const response = await fetch('/api/ai/optimize-portfolio', {
    method: 'POST',
    headers,
    body: JSON.stringify({ portfolio, macro, language, investorType }),
    credentials: 'include',
    signal
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${response.status}`);
  }

  if (!response.body) throw new Error('No response body from optimization server.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult: OptimizationData | null = null;

// eslint-disable-next-line
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const jsonStr = line.replace('data: ', '').trim();
        if (jsonStr === '[DONE]') continue;
        try {
          const payload = JSON.parse(jsonStr);
          if (payload.type === 'progress' && onProgress) {
            onProgress(payload.percent, payload.message);
          } else if (payload.type === 'result') {
            finalResult = payload.data;
          } else if (payload.type === 'error') {
            throw new Error(payload.message);
          }
        } catch (e) {
          console.warn('[Optimization Stream] Parse error:', e);
        }
      }
    }
  }

  if (!finalResult) throw new Error('Optimization failed: No result data received.');
  return finalResult;
}
